import type { IF360Currency, TCurrencyValue } from "./types";
import CurrencyJs from "currency.js";
import { injectable } from "inversify";
import "reflect-metadata";

@injectable()
export class F360CurrencyJs implements IF360Currency {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly config: Record<string, any> = {};

  constructor() {
    this.config = {
      symbol: "R$ ",
      decimal: ".",
      separator: ","
    };
  }

  toOnlyMoney(value: TCurrencyValue): string {
    return this.toFormat(this.getCurrency(value).value)
      .replace(/\s/g, "")
      .replace(/^R\$/, "");
  }

  private getCurrency(value: TCurrencyValue) {
    return CurrencyJs(value, this.config);
  }

  toFormat(value: TCurrencyValue) {
    return this.getCurrency(value).format({
      decimal: ",",
      separator: "."
    });
  }

  toFormatRaw(value: TCurrencyValue): string {
    return CurrencyJs(value).format({
      decimal: ",",
      separator: ".",
      symbol: ""
    });
  }

  toMoney(value: TCurrencyValue): string {
    return this.toFormat(this.getCurrency(value).value);
  }

  currencyValue(value: TCurrencyValue) {
    return this.getCurrency(value).value;
  }

  intValue(value: TCurrencyValue): number {
    return this.getCurrency(value).intValue;
  }

  add(value: TCurrencyValue, additive: TCurrencyValue): string {
    return this.toFormat(this.getCurrency(value).add(additive).value);
  }

  subtract(value: TCurrencyValue, subtractive: TCurrencyValue): string {
    return this.toFormat(this.getCurrency(value).subtract(subtractive).value);
  }

  multiply(value: TCurrencyValue, multiplier: TCurrencyValue): string {
    return this.toFormat(this.getCurrency(value).multiply(multiplier).value);
  }

  divide(value: TCurrencyValue, divider: TCurrencyValue): string {
    return this.toFormat(this.getCurrency(value).divide(divider).value);
  }

  distribute(value: TCurrencyValue, divider: number): string[] {
    const currencyArray = this.getCurrency(value).distribute(divider);

    return currencyArray.map((currency) => currency.format());
  }

  distributeRaw(value: TCurrencyValue, divider: number): string[] | number[] {
    return this.getCurrency(value)
      .distribute(divider)
      .map((result) => Number(result));
  }

  addSeparatorNumber(value: TCurrencyValue): string {
    const customCurrency = CurrencyJs(value, {
      precision: 0,
      symbol: "",
      decimal: ".",
      separator: "."
    });

    return customCurrency.format();
  }
}
